import { useState, useEffect, useCallback, RefObject } from 'react';

interface VideoState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  isFullscreen: boolean;
  buffered: number;
  hasEnded: boolean;
  play: () => void;
  pause: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  toggleFullscreen: () => void;
}

export const useVideoState = (videoRef: RefObject<HTMLVideoElement>, isOpen: boolean): VideoState => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [buffered, setBuffered] = useState(0);
  const [hasEnded, setHasEnded] = useState(false);

  // Play video
  const play = useCallback(async () => {
    if (videoRef.current) {
      try {
        await videoRef.current.play();
        setIsPlaying(true);
        // Only reset ended state if we're playing from near the beginning
        if (videoRef.current.currentTime < 5) {
          setHasEnded(false);
        }
      } catch (error) {
        // Silently handle play errors (e.g., autoplay policies)
      }
    }
  }, [videoRef]);

  // Pause video
  const pause = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [videoRef]);

  // Seek to time
  const seek = useCallback((time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, [videoRef]);

  // Set volume
  const setVolume = useCallback((newVolume: number) => {
    if (videoRef.current) {
      const clampedVolume = Math.max(0, Math.min(1, newVolume));
      videoRef.current.volume = clampedVolume;
      setVolumeState(clampedVolume);
      if (clampedVolume > 0 && isMuted) {
        setIsMuted(false);
      }
    }
  }, [videoRef, isMuted]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  }, [videoRef]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      const container = videoRef.current?.parentElement?.parentElement;
      if (container?.requestFullscreen) {
        container.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, [videoRef]);

  // Robust event listener setup with proper lifecycle management
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let rafId: number;

    const setupListeners = () => {
      const video = videoRef.current;
      if (!video) {
        // Retry after a short delay if video isn't ready
        timeoutId = setTimeout(setupListeners, 50);
        return;
      }

      // Use RAF for smooth time updates to prevent glitching
      const handleTimeUpdate = () => {
        rafId = requestAnimationFrame(() => {
          setCurrentTime(video.currentTime);
        });
      };

      const handleDurationChange = () => {
        const duration = video.duration;
        if (isFinite(duration) && duration > 0) {
          setDuration(duration);
        }
      };

      const handleLoadedMetadata = () => {
        const duration = video.duration;
        if (isFinite(duration) && duration > 0) {
          setDuration(duration);
        }
        setCurrentTime(video.currentTime);
      };

      const handlePlay = () => {
        setIsPlaying(true);
        if (video.currentTime < 5) {
          setHasEnded(false);
        }
      };

      const handlePause = () => {
        setIsPlaying(false);
      };

      const handleVolumeChange = () => {
        setVolumeState(video.volume);
        setIsMuted(video.muted);
      };

      const handleProgress = () => {
        if (video.buffered.length > 0) {
          const bufferedEnd = video.buffered.end(video.buffered.length - 1);
          setBuffered(bufferedEnd);
        }
      };

      const handleEnded = () => {
        setHasEnded(true);
        setIsPlaying(false);
      };

      const handleLoadStart = () => {
        setHasEnded(false);
        setCurrentTime(0);
      };

      // Add all event listeners
      const events = [
        ['timeupdate', handleTimeUpdate],
        ['durationchange', handleDurationChange],
        ['loadedmetadata', handleLoadedMetadata],
        ['play', handlePlay],
        ['pause', handlePause],
        ['volumechange', handleVolumeChange],
        ['progress', handleProgress],
        ['ended', handleEnded],
        ['loadstart', handleLoadStart]
      ] as const;

      events.forEach(([event, handler]) => {
        video.addEventListener(event, handler);
      });

      // Initialize state immediately
      if (isFinite(video.duration) && video.duration > 0) {
        setDuration(video.duration);
      }
      setCurrentTime(video.currentTime);
      setVolumeState(video.volume);

      // Sync with current muted state instead of forcing it
      setIsMuted(video.muted);

      // Return cleanup function
      return () => {
        if (rafId) cancelAnimationFrame(rafId);
        events.forEach(([event, handler]) => {
          video.removeEventListener(event, handler);
        });
      };
    };

    let cleanup: (() => void) | undefined;

    if (isOpen) {
      cleanup = setupListeners();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (rafId) cancelAnimationFrame(rafId);
      if (cleanup) cleanup();
    };
  }, [videoRef, isOpen]);

  return {
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isFullscreen,
    buffered,
    hasEnded,
    play,
    pause,
    seek,
    setVolume,
    toggleMute,
    toggleFullscreen
  };
};